/**
 * FeedbackButtons — Like/dislike inline + feedback dialog.
 *
 * Toolbar: small square buttons (28px, rounded-8px).
 * Dialog: HeadlessUI Dialog + Transition, consistent with MetadataModal.
 */

import React, { useState, useEffect, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import { ThumbsUp, ThumbsDown } from "lucide-react";

const FeedbackButtons = ({
  chatId,
  questionId,
  addFeedback,
  addFeedback2,
  disabled,
  assistantColors,
  retrievalId,
  selectedRetrieval,
}) => {
  const { t } = useTranslation();
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [comment, setComment] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLike, setIsLike] = useState(false);

  // Reset on retrieval change
  useEffect(() => {
    setFeedbackSent(false);
    resetForm();
  }, [retrievalId, selectedRetrieval]);

  // Listen for reset event (new chat)
  useEffect(() => {
    const reset = () => {
      setFeedbackSent(false);
      resetForm();
    };
    document.addEventListener("resetFeedbackState", reset);
    return () => document.removeEventListener("resetFeedbackState", reset);
  }, []);

  const resetForm = () => {
    setLiked(false);
    setDisliked(false);
    setIsLike(false);
    setComment("");
  };

  const handleLike = () => {
    if (disabled || feedbackSent) return;
    setLiked(true);
    setDisliked(false);
    setIsLike(true);
    setShowDialog(true);
  };

  const handleDislike = () => {
    if (disabled || feedbackSent) return;
    setLiked(false);
    setDisliked(true);
    setIsLike(false);
    setShowDialog(true);
  };

  const sendFeedback = async () => {
    if (!questionId) {
      setShowDialog(false);
      return;
    }

    try {
      setLoading(true);
      await addFeedback(questionId, isLike, comment);
      setFeedbackSent(true);
      setShowDialog(false);
    } catch (error) {
      console.error("Feedback error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowDialog(false);
    resetForm();
  };

  return (
    <>
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-150 cursor-pointer disabled:opacity-35 disabled:cursor-default border-none ${
            liked
              ? "bg-gray-900 text-white"
              : "bg-transparent text-gray-400 hover:bg-gray-100 hover:text-gray-900"
          }`}
          onClick={handleLike}
          disabled={disabled || feedbackSent}
          title={t("feedback")}
        >
          <ThumbsUp size={13} fill={liked ? "currentColor" : "none"} />
        </button>
        <button
          type="button"
          className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-150 cursor-pointer disabled:opacity-35 disabled:cursor-default border-none ${
            disliked
              ? "bg-red-500 text-white"
              : "bg-transparent text-gray-400 hover:bg-gray-100 hover:text-gray-900"
          }`}
          onClick={handleDislike}
          disabled={disabled || feedbackSent}
          title={t("feedback")}
        >
          <ThumbsDown size={13} fill={disliked ? "currentColor" : "none"} />
        </button>
      </div>

      {/* Feedback dialog — HeadlessUI Dialog consistent with MetadataModal */}
      <Transition.Root show={showDialog} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-50">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className="relative transform bg-white rounded-2xl text-left transition-all w-full max-w-sm shadow-xl p-6 border"
                  style={{ borderColor: assistantColors?.borderColor || "#e5e7eb" }}
                >
                  <Dialog.Title as="h2" className="text-lg font-medium text-gray-900 mb-1">
                    {t("feedbackTitle")}
                  </Dialog.Title>
                  <p className="text-sm text-gray-500 mb-4">
                    {t("feedbackDescription")}
                  </p>
                  <textarea
                    className="w-full border border-gray-200 rounded-lg p-2.5 text-sm resize-none outline-none focus:border-gray-400 font-sans box-border"
                    rows={3}
                    placeholder={t("feedbackComment")}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                  <div className="flex justify-end gap-3 mt-4">
                    <button
                      type="button"
                      className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={handleClose}
                    >
                      {t("cancel")}
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition-colors cursor-pointer disabled:opacity-50"
                      style={{ backgroundColor: assistantColors?.primaryColor || "#111827" }}
                      onClick={sendFeedback}
                      disabled={loading}
                    >
                      {t("send")}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export { FeedbackButtons };
